import React, { useEffect, useState } from "react";
import { api } from "../../lib/api.js";
import { Database, Loader2, RefreshCw, Check, AlertTriangle } from "lucide-react";

/**
 * DemoSeedPanel — admin control to wipe business data and reseed a
 * curated demo dataset. Shows current record counts and summary after reset.
 */
export default function DemoSeedPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/admin/seed-demo/preview");
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha a obter contagens.");
    }
  };

  useEffect(() => { loadPreview(); }, []);

  const runSeed = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/admin/seed-demo");
      setResult(data.summary);
      await loadPreview();
    } catch (e) {
      setError(e?.response?.data?.detail || "Falha a recarregar demo.");
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  const LABELS = {
    service_cases: "SOMs",
    parts_requests: "PCPs",
    classification_requests: "PECs",
    vehicle_registrations: "VRs",
    body_shop_cases: "Carroçaria (SWC)",
    maintenance_plans: "Planos Manut. (MPM)",
    warranty_claims: "Garantias",
    complaints: "Reclamações",
    surveys: "Inquéritos",
    parts_bench_orders: "Peças Balcão (PBO)",
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-soft-card" data-testid="demo-seed-panel">
      <div className="flex items-start gap-3 mb-4">
        <Database className="w-6 h-6 text-ronil-navy mt-0.5" />
        <div className="flex-1">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Dataset de demonstração</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Carrega um conjunto limpo e representativo (~10 SOMs em diferentes estágios + registos ligados para todos os módulos). Útil antes de uma apresentação ou sessão de UAT com colegas.
          </p>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 mb-4 flex gap-2">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <b>Ação destrutiva.</b> Apaga <b>todos</b> os SOMs, PCPs, PECs, SWCs, MPMs, Garantias, Reclamações, Inquéritos e tarefas existentes. Utilizadores, marcas, filiais e logs de auditoria <i>não</i> são afectados.
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
        {preview && Object.entries(preview).map(([k, v]) => (
          <div key={k} className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-heading font-semibold leading-tight">{LABELS[k] || k}</div>
            <div className="font-heading text-2xl font-bold text-ronil-navy tabular-nums mt-1" data-testid={`seed-count-${k}`}>{v}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 mb-4" data-testid="demo-seed-error">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 mb-4" data-testid="demo-seed-success">
          <div className="flex items-center gap-2 font-semibold mb-1">
            <Check className="w-4 h-4" /> Dataset recarregado com sucesso
          </div>
          <div className="text-emerald-800/90 text-xs">
            {Object.entries(result).map(([k, v]) => `${v} ${LABELS[k] || k}`).join(" · ")}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md px-4 py-2 text-sm font-heading font-semibold shadow-sm hover:shadow-elevation transition-all"
            data-testid="demo-seed-start"
          >
            <RefreshCw className="w-4 h-4" />
            Recarregar dados de demo
          </button>
        ) : (
          <>
            <button
              onClick={runSeed}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 text-sm font-heading font-semibold transition-colors"
              data-testid="demo-seed-confirm"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              Sim, apagar tudo e recarregar
            </button>
            <button
              onClick={() => setConfirming(false)}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md px-4 py-2 text-sm font-medium transition-colors"
              data-testid="demo-seed-cancel"
            >
              Cancelar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
